import { useEffect, useRef, useState } from 'react';
import { useForegroundInterval } from './useForegroundInterval';

const FINISHED_TEXT = '00 : 00 : 00';

const formatRemainingTime = (finishedAt: string): string => {
  const diff = new Date(finishedAt).getTime() - Date.now();
  if (diff <= 0) return FINISHED_TEXT;

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')} : ${String(seconds).padStart(2, '0')}`;
};

type UseCountdownOptions = {
  onFinish?: () => void;
};

export const useCountdown = (
  finishedAt?: string | null,
  options?: UseCountdownOptions,
) => {
  const [timeText, setTimeText] = useState(() =>
    finishedAt ? formatRemainingTime(finishedAt) : null,
  );
  const onFinishRef = useRef(options?.onFinish);
  onFinishRef.current = options?.onFinish;

  useEffect(() => {
    if (finishedAt != null) {
      const formatted = formatRemainingTime(finishedAt);
      setTimeText(formatted);
      if (formatted === FINISHED_TEXT) {
        onFinishRef.current?.();
      }
    }
  }, [finishedAt]);

  const isFinished = timeText === FINISHED_TEXT;

  useForegroundInterval(() => {
    if (finishedAt == null) return;
    const remaining = formatRemainingTime(finishedAt);
    setTimeText(remaining);
    if (remaining === FINISHED_TEXT) {
      onFinishRef.current?.();
    }
  }, finishedAt != null && !isFinished ? 1000 : null);

  return { timeText, isFinished };
};
